import React, { useMemo, useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { generatePerformanceInsights } from '../../../services/aiAnalysisService';
import Button from '../../../components/ui/Button';

const PerformanceInsights = ({ players, coaches, goalkeepers }) => {
  const [aiInsights, setAiInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [useAI, setUseAI] = useState(false);

  const staticInsights = useMemo(() => {
    const allInsights = [];

    // Player insights
    const topScorer = players?.reduce((max, p) => (p?.goalsScored || 0) > (max?.goalsScored || 0) ? p : max, {});
    if (topScorer?.fullName) {
      allInsights?.push({
        id: 1,
        type: 'exceptional',
        icon: 'Trophy',
        color: 'bg-yellow-500/10 text-yellow-600',
        title: 'Top Goal Scorer',
        description: `${topScorer?.fullName} leads with ${topScorer?.goalsScored} goals, showing exceptional attacking prowess.`,
        metric: `${topScorer?.goalsScored} Goals`,
        category: 'Player Performance'
      });
    }

    const topAssister = players?.reduce((max, p) => (p?.assists || 0) > (max?.assists || 0) ? p : max, {});
    if (topAssister?.fullName && topAssister?.assists > 0) {
      allInsights?.push({
        id: 2,
        type: 'emerging',
        icon: 'TrendingUp',
        color: 'bg-primary/10 text-primary',
        title: 'Playmaker Excellence',
        description: `${topAssister?.fullName} demonstrates creative excellence with ${topAssister?.assists} assists.`,
        metric: `${topAssister?.assists} Assists`,
        category: 'Player Performance'
      });
    }

    // Transfer market insights
    const availablePlayers = players?.filter(p => p?.availableForTransfer)?.length;
    if (availablePlayers > 0) {
      allInsights?.push({
        id: 3,
        type: 'market',
        icon: 'DollarSign',
        color: 'bg-success/10 text-success',
        title: 'Transfer Market Activity',
        description: `${availablePlayers} player${availablePlayers !== 1 ? 's are' : ' is'} currently available for transfer, indicating active market opportunities.`,
        metric: `${availablePlayers} Available`,
        category: 'Market Intelligence'
      });
    }

    // Coach insights
    const bestCoach = coaches?.reduce((max, c) => {
      const maxWinRate = (max?.gamesWon || 0) / (max?.totalGamesManaged || 1);
      const currentWinRate = (c?.gamesWon || 0) / (c?.totalGamesManaged || 1);
      return currentWinRate > maxWinRate ? c : max;
    }, {});
    if (bestCoach?.fullName && bestCoach?.totalGamesManaged > 0) {
      const winRate = ((bestCoach?.gamesWon / bestCoach?.totalGamesManaged) * 100)?.toFixed(1);
      allInsights?.push({
        id: 4,
        type: 'exceptional',
        icon: 'Award',
        color: 'bg-secondary/10 text-secondary',
        title: 'Top Performing Coach',
        description: `${bestCoach?.fullName} maintains an impressive ${winRate}% win rate across ${bestCoach?.totalGamesManaged} matches.`,
        metric: `${winRate}% Win Rate`,
        category: 'Coach Performance'
      });
    }

    // Goalkeeper insights
    const bestGoalkeeper = goalkeepers?.reduce((max, g) => {
      const maxRate = (max?.cleanSheets || 0) / (max?.gamesPlayed || 1);
      const currentRate = (g?.cleanSheets || 0) / (g?.gamesPlayed || 1);
      return currentRate > maxRate ? g : max;
    }, {});
    if (bestGoalkeeper?.fullName && bestGoalkeeper?.gamesPlayed > 0) {
      const cleanSheetRate = ((bestGoalkeeper?.cleanSheets / bestGoalkeeper?.gamesPlayed) * 100)?.toFixed(1);
      allInsights?.push({
        id: 5,
        type: 'exceptional',
        icon: 'Shield',
        color: 'bg-accent/10 text-accent',
        title: 'Defensive Excellence',
        description: `${bestGoalkeeper?.fullName} showcases outstanding goalkeeping with ${cleanSheetRate}% clean sheet rate.`,
        metric: `${bestGoalkeeper?.cleanSheets} Clean Sheets`,
        category: 'Goalkeeper Performance'
      });
    }

    // Emerging talent pattern
    const youngTalent = players?.filter(p => p?.age && p?.age < 23 && (p?.goalsScored || 0) > 5);
    if (youngTalent?.length > 0) {
      allInsights?.push({
        id: 6,
        type: 'emerging',
        icon: 'Sparkles',
        color: 'bg-purple-500/10 text-purple-600',
        title: 'Emerging Young Talent',
        description: `${youngTalent?.length} player${youngTalent?.length !== 1 ? 's' : ''} under 23 showing exceptional goal-scoring ability, indicating strong future prospects.`,
        metric: `${youngTalent?.length} Players`,
        category: 'Talent Development'
      });
    }

    // International experience
    const internationalPlayers = players?.filter(p => (p?.internationalCaps || 0) > 0)?.length;
    if (internationalPlayers > 0) {
      allInsights?.push({
        id: 7,
        type: 'pattern',
        icon: 'Globe',
        color: 'bg-blue-500/10 text-blue-600',
        title: 'International Experience',
        description: `${internationalPlayers} player${internationalPlayers !== 1 ? 's have' : ' has'} international caps, demonstrating high-level competitive experience.`,
        metric: `${internationalPlayers} Players`,
        category: 'Experience Level'
      });
    }

    // Database growth
    const totalRecords = players?.length + coaches?.length + goalkeepers?.length;
    if (totalRecords > 0) {
      allInsights?.push({
        id: 8,
        type: 'pattern',
        icon: 'Database',
        color: 'bg-indigo-500/10 text-indigo-600',
        title: 'Database Growth',
        description: `Performance database now contains ${totalRecords} comprehensive records across all talent categories.`,
        metric: `${totalRecords} Records`,
        category: 'Platform Analytics'
      });
    }

    return allInsights;
  }, [players, coaches, goalkeepers]);

  const fetchAIInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await generatePerformanceInsights({ players, coaches, goalkeepers });
      if (result?.error) {
        setError(result?.error);
        setUseAI(false);
      } else {
        setAiInsights(result?.insights?.map((insight, index) => ({
          ...insight,
          id: `ai-${index}`,
          color: getColorForType(insight?.type)
        })));
      }
    } catch (err) {
      setError('Failed to generate AI insights');
      setUseAI(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (useAI && aiInsights?.length === 0 && !loading) {
      fetchAIInsights();
    }
  }, [useAI]);

  const getColorForType = (type) => {
    switch (type) {
      case 'exceptional': return 'bg-yellow-500/10 text-yellow-600';
      case 'emerging': return 'bg-primary/10 text-primary';
      case 'market': return 'bg-success/10 text-success';
      case 'pattern': return 'bg-indigo-500/10 text-indigo-600';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'exceptional': return 'Exceptional Performance';
      case 'emerging': return 'Emerging Pattern';
      case 'market': return 'Market Insight';
      case 'pattern': return 'Trend Analysis';
      default: return 'Insight';
    }
  };

  const displayInsights = useAI ? aiInsights : staticInsights;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Performance Insights</h2>
          <p className="text-sm text-muted-foreground">
            {useAI ? 'AI-powered analytics' : 'Rule-based analytics'} identifying emerging patterns, exceptional performers, and talent trajectories
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setUseAI(!useAI);
              if (!useAI && aiInsights?.length === 0) {
                fetchAIInsights();
              }
            }}
            variant={useAI ? 'primary' : 'outline'}
            size="sm"
            disabled={loading}
          >
            <Icon name={useAI ? 'Sparkles' : 'Brain'} size={16} />
            {useAI ? 'AI Insights' : 'Enable AI'}
          </Button>
          {useAI && aiInsights?.length > 0 && (
            <Button
              onClick={fetchAIInsights}
              variant="outline"
              size="sm"
              disabled={loading}
            >
              <Icon name="RefreshCw" size={16} />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-600">
            <Icon name="AlertCircle" size={20} />
            <p className="text-sm font-medium">{error}</p>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-3">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground">Generating AI insights...</p>
          </div>
        </div>
      )}

      {!loading && displayInsights?.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {displayInsights?.map((insight) => (
            <div
              key={insight?.id}
              className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg ${insight?.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={insight?.icon} size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-lg font-bold text-foreground">{insight?.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${insight?.color}`}>
                      {getTypeLabel(insight?.type)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{insight?.description}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">{insight?.category}</span>
                    <span className="text-sm font-bold text-foreground">{insight?.metric}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : !loading && (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="Lightbulb" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Insights Available</h3>
          <p className="text-muted-foreground">Add more performance records to generate insights and analytics.</p>
        </div>
      )}

      {/* Summary Stats */}
      {!loading && displayInsights?.length > 0 && (
        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name={useAI ? 'Sparkles' : 'BarChart3'} size={24} className="text-primary" />
            <h3 className="text-xl font-bold text-foreground">Insight Summary</h3>
            {useAI && (
              <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                AI-Powered
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Insights</p>
              <p className="text-2xl font-bold text-foreground">{displayInsights?.length}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Exceptional</p>
              <p className="text-2xl font-bold text-foreground">
                {displayInsights?.filter(i => i?.type === 'exceptional')?.length}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Emerging</p>
              <p className="text-2xl font-bold text-foreground">
                {displayInsights?.filter(i => i?.type === 'emerging')?.length}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Patterns</p>
              <p className="text-2xl font-bold text-foreground">
                {displayInsights?.filter(i => i?.type === 'pattern')?.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceInsights;